import { TelegramAltIcon } from "@/components/Icons"
import { Modal } from "@/components/Modal"
import { ClusterStatus } from "@/domain/cluster/constants"
import { NIL_UUID } from "@/domain/resources/constants"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import {
  type ClusterJsonldReadItemClusterItem,
  type InterBuPostInterBuClustersInputJsonld,
  type SharedDataJsonldReadCollectionSharedData,
  getGetUserSharedDataQueryKey,
  getGetUserSharedDatasQueryKey,
  usePostInterbuClusters,
} from "@/networking/api"
import { useGetReactSelectStyles } from "@/theme/components-style-overrides/reactselect-definition"
import { Orders } from "@/utils/filters/types"
import { isNotEmptyArray, isNotNullNorUndefined } from "@/utils/ts-utils"
import { Box, Button, Flex, FormControl, FormLabel, ModalBody, ModalFooter } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import ReactSelect from "react-select"

import { normalizedNameFilter } from "@/utils/search/matchesNormalizedName"
import { useGetClusterInfiniteQuery } from "../ContentTabs/clustersTab/ClusterTableLayout/hook/useGetClusterInfiniteQuery"
import { RequiredErrorMessage } from "../RequiredErrorMessage"
import SharedEntityName from "./SharedEntityName"

type FormType = InterBuPostInterBuClustersInputJsonld

type Props = {
  isOpen: boolean
  onClose: (refresh?: boolean) => void
  sharedEntity: SharedDataJsonldReadCollectionSharedData
}

export default function ShareDuplicateModal({ isOpen, onClose, sharedEntity }: Props) {
  const intl = useIntl()

  const defaultValues: FormType = {
    cluster_ids: [],
  }

  const [clustersSelected, setClustersSelected] = useState<ClusterJsonldReadItemClusterItem[]>([])
  const clusterIdsSelected = useMemo(() => clustersSelected.map(c => c.id), [clustersSelected])

  const modalForm = useForm<FormType>({
    defaultValues,
  })
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = modalForm

  // Clusters request / options

  const [searchTermCluster, setSearchTermCluster] = useState("")

  const { clusters, isFetchingData: isLoadingClusters } = useGetClusterInfiniteQuery({
    search: searchTermCluster,
    itemsPerPage: 20,
    "status.code": ClusterStatus.ACTIVE,
    "order[name]": Orders.ASC,
    withMinProps: true,
  })
  const clusterOptions = useMemo(
    () =>
      (clusters ?? [])
        .filter(c => !clusterIdsSelected.includes(c.id))
        .map(c => {
          return {
            value: c,
            label: c.name ?? "unknown",
          }
        }),
    [clusters, clusterIdsSelected]
  )
  const clusterOptionSelected = useMemo(() => {
    return isNotEmptyArray(clustersSelected)
      ? clustersSelected.map(c => ({
          value: c,
          label: c.name ?? "unknown",
        }))
      : []
  }, [clustersSelected])

  let updateClusterSearchItv: NodeJS.Timeout
  const onClusterInputChange = (text: string) => {
    if (searchTermCluster === text) return
    if (isNotNullNorUndefined(updateClusterSearchItv)) clearTimeout(updateClusterSearchItv)
    updateClusterSearchItv = setTimeout(() => setSearchTermCluster(text), 500)
  }

  const { mutate, isPending } = usePostInterbuClusters()
  const queryClient = useQueryClient()
  const genericErrorToast = useGenericErrorToast()

  const postShareRequest = (formData: FormType) => {
    const data: InterBuPostInterBuClustersInputJsonld = { ...formData }

    mutate(
      {
        id: sharedEntity.interBu?.id ?? NIL_UUID,
        data,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDataQueryKey(sharedEntity.id),
          })
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDatasQueryKey(),
          })
          onClose(true)
        },
        onError: genericErrorToast,
      }
    )
  }

  return (
    <Modal
      {...{ isOpen, onClose }}
      title={<FormattedMessage id="userSharedData.duplicateRequest" />}
      scrollBehavior="outside"
    >
      <form onSubmit={handleSubmit(postShareRequest)}>
        <ModalBody>
          <Flex flexDir="column" gap="6">
            <Box>
              <FormattedMessage id="userSharedData.duplicateRequest.message" />
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.interbu.people.name" />
              </FormLabel>
              <SharedEntityName item={sharedEntity} displayInputLike={true} />
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.interbu.modal.steps.2.clusterRecipient" />
              </FormLabel>
              <FormControl isInvalid={!!errors.cluster_ids}>
                <Controller
                  control={control}
                  name="cluster_ids"
                  render={({ field: { onChange }, fieldState }) => (
                    <ReactSelect
                      {...register("cluster_ids", { required: true })}
                      isMulti={true}
                      filterOption={normalizedNameFilter}
                      options={clusterOptions}
                      defaultValue={clusterOptionSelected}
                      onChange={newVal => {
                        const newIds = [...newVal.values()].map(c => c.value?.id)
                        onChange(newIds)
                        setClustersSelected([...newVal].map(c => c.value))
                      }}
                      onInputChange={onClusterInputChange}
                      styles={useGetReactSelectStyles(fieldState)}
                      isLoading={isLoadingClusters}
                      loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                    />
                  )}
                />
                <RequiredErrorMessage />
              </FormControl>
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button display="flex" gap="2" type="submit" isLoading={isPending}>
            <FormattedMessage id="send" />
            <TelegramAltIcon boxSize={4} />
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
